import {
  ShopRimBrandListApiResponse,
  ShopRimInventoryDimensionsListApiResponse,
  ShopRimListApiResponse,
  SupplierProductStatus,
  type RimInventoryFilterCountsApiData,
  type RimInventoryListApiData
} from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { URLKeys } from 'noddi-async';
import {
  enrichFiltersWithCounts,
  FilterControl,
  FilterOption,
  FilterOptionSource,
  filterUnitFor,
  filterUrlState,
  mapFilterControls,
  useFilterSpecValue,
  useFilterUnits,
  usePagination,
  type SharedQuery
} from 'noddi-ui-web';
import {
  INVENTORY_CATALOG_CHANNEL_OPTIONS,
  RIM_COLOR_FINISH_OPTIONS,
  RIM_ITEM_NOTE_OPTIONS,
  SUPPLIER_PRODUCT_STATUS_OPTIONS
} from 'noddi-util';
import { useVehicleCatalogNodeOptions, VEHICLE_CATALOG_NODE_FILTER_TEXT } from '../Car/useVehicleCatalogNodeFilter';
import { toFilterOptionSource, useFilterOptionsGet } from '../useFilterOptionsGet';
import { useTireShopSupplierOptions } from './useTireInventoryFilters';

type RimListQuery = NonNullable<RimInventoryListApiData['query']>;

type RimCountsQuery = NonNullable<RimInventoryFilterCountsApiData['query']>;

/** The list and its facet counts take the same filters, so a unit writes only params both accept. */
type RimInventoryFilterInput = SharedQuery<RimListQuery, RimCountsQuery>;

const rimFilterUnit = filterUnitFor<RimInventoryFilterInput>();

const RIM_SUPPLIER_PRODUCT_STATUS_FILTER_OPTIONS: FilterOption<SupplierProductStatus>[] =
  SUPPLIER_PRODUCT_STATUS_OPTIONS.map((opt) => ({
    label: opt.value,
    value: opt.value
  }));

/** Rim inventory filters, with facet counts; spread `apiInput` into the list query. */
export const useRimInventoryFilters = () => {
  const brandFilter = unit.multiSelect('brandIds', {
    title: 'Rim brand',
    source: useShopRimBrandOptions(),
    exclude: true
  });
  // One catalog filter replaces the OEM make and model pair: a mark links to a catalog node, make or model.
  const oemCatalogFilter = unit.multiSelect('originalEquipmentMarkCatalogNodeIds', {
    ...VEHICLE_CATALOG_NODE_FILTER_TEXT,
    source: useVehicleCatalogNodeOptions(),
    exclude: true
  });
  // Rim models of the picked brands.
  const { brandIds } = useFilterSpecValue(brandFilter);
  const rims = useShopRimOptions({ brandIds });
  const dimensions = useRimInventoryDimensionOptions();
  const suppliers = useTireShopSupplierOptions();

  const filters = useFilterUnits([
    inStockFilter,
    supplierProductStatusFilter,
    catalogChannelFilter,
    colorFinishFilter,
    itemNotesFilter,
    dimensionFilter(dimensions),
    brandFilter,
    unit.multiSelect('rimIds', {
      title: 'Rim model',
      description: 'Filter by rim brand and model',
      source: rims,
      exclude: true
    }),
    oemCatalogFilter,
    unit.multiSelect('supplierIds', { title: 'Suppliers', source: suppliers, exclude: true }),
    unit.numberRange('lugSeatAngle', {
      title: 'Lug seat angle',
      description: 'Filter by lug seat angle (degrees)',
      exclude: true
    }),
    unit.numberRange('rimBoltPatternBolts', {
      title: 'Bolt pattern (bolts)',
      description: 'Filter by number of bolt holes',
      exclude: true
    }),
    unit.numberRange('rimBoltPatternDiameter', {
      title: 'Bolt pattern diameter',
      description: 'Filter by bolt circle diameter (mm)',
      exclude: true
    }),
    unit.numberRange('rimCenterBore', {
      title: 'Center bore',
      description: 'Filter by center bore diameter (mm)',
      exclude: true
    }),
    unit.numberRange('rimDiameter', {
      title: 'Rim diameter',
      description: 'Filter by rim diameter (inches)',
      exclude: true
    }),
    unit.numberRange('rimOffset', { title: 'Rim offset', description: 'Filter by rim offset (mm)', exclude: true }),
    unit.numberRange('rimWidth', { title: 'Rim width', description: 'Filter by rim width', exclude: true }),
    unit.boolean('hasRimImage', { title: 'Has rim image', description: 'Only show rims with product image' })
  ]);

  const { data: filterCounts, error: filterCountsError } = useFilterOptionsGet({
    type: URLKeys.getRimInventoryFilterCounts,
    input: filters.apiInput,
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });
  // Facet counts are secondary — list still works if filter-counts fails.
  void filterCountsError;

  // Keyed by control id, which is the param the control writes.
  const countMap = {
    inStock: filterCounts?.inStock,
    supplierProductStatuses: filterCounts?.supplierProductStatuses,
    catalogChannels: filterCounts?.catalogChannels,
    colorFinish: filterCounts?.colorFinish,
    itemNotes: filterCounts?.itemNotes,
    brandIds: filterCounts?.brands,
    rimIds: filterCounts?.rimModels,
    originalEquipmentMarkCatalogNodeIds: filterCounts?.originalEquipmentMarkCatalogNodes,
    supplierIds: filterCounts?.suppliers,
    hasRimImage: filterCounts?.hasRimImage
  };
  return mapFilterControls(filters, (control) => enrichFiltersWithCounts([control], countMap)[0] ?? control);
};

const unit = rimFilterUnit;

const IN_STOCK_URL_KEY = filterUrlState.urlKey('inStock');

const quantityInStockFilter = unit.numberRange('quantityInStock', {
  title: 'Quantity in stock',
  description: 'Filter by quantity range',
  exclude: true
});

/**
 * In stock is a shortcut onto the quantity floor: on, with no minimum typed, it sends a minimum of 1. One unit,
 * because both controls write `quantityInStockGte`; the range is resolved inside it.
 */
const inStockFilter = unit.compose(
  'rimInStock',
  { [IN_STOCK_URL_KEY]: filterUrlState.boolean, ...quantityInStockFilter.urlParams },
  (values, setValues) => {
    const stored = values[IN_STOCK_URL_KEY] as boolean | null | undefined;
    // In stock until the user picks; clearing then lists everything.
    const inStock = filterUrlState.storedValue(stored, true);
    const quantity = quantityInStockFilter.resolve(values, setValues);
    const inStockControl: FilterControl = {
      id: 'inStock',
      type: 'boolean',
      title: 'In Stock',
      description: 'Only show rims with quantity in stock',
      value: inStock ?? null,
      onChange: (value) => setValues(() => ({ [IN_STOCK_URL_KEY]: filterUrlState.toStored(value, true) })),
      reset: () => setValues(() => ({ [IN_STOCK_URL_KEY]: undefined })),
      isInitial: stored === undefined
    };
    const { quantityInStockGte } = quantity.apiInput;
    return {
      controls: [inStockControl, ...quantity.controls],
      params: {
        ...quantity.apiInput,
        inStock,
        quantityInStockGte: inStock === true && quantityInStockGte === undefined ? 1 : quantityInStockGte
      }
    };
  }
);

const supplierProductStatusFilter = unit.multiSelect('supplierProductStatuses', {
  title: 'Supplier product status',
  description: 'Status of product in supplier inventory',
  options: RIM_SUPPLIER_PRODUCT_STATUS_FILTER_OPTIONS,
  defaults: [SupplierProductStatus.AVAILABLE],
  exclude: true
});

const catalogChannelFilter = unit.multiSelect('catalogChannels', {
  title: 'Catalog channel',
  description: 'How the rim is sourced (EDI feed, adapter resale, or manual)',
  options: INVENTORY_CATALOG_CHANNEL_OPTIONS,
  exclude: true
});

const colorFinishFilter = unit.multiSelect('colorFinish', {
  title: 'Color finish',
  description: 'Filter by rim color finish',
  options: RIM_COLOR_FINISH_OPTIONS,
  exclude: true
});

const itemNotesFilter = unit.multiSelect('itemNotes', {
  title: 'Item notes',
  description: 'Filter by item notes (e.g. OEC, TPMS)',
  options: RIM_ITEM_NOTE_OPTIONS,
  exclude: true
});

const dimensionFilter = (source: FilterOptionSource<string>) =>
  unit.multiSelect('dimension', {
    title: 'Dimensions',
    description: 'Rim dimension (e.g. 5.5X14, 7X17)',
    source,
    exclude: true
  });

const useShopRimBrandOptions = (): FilterOptionSource<number> => {
  const { pageIndex, pageSize } = usePagination({ initialPageSize: 50 });

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedShopRimBrands,
    input: { pageIndex, pageSize },
    queryConfig: { staleTime: Infinity, select: toShopRimBrandOptions }
  });

  return toFilterOptionSource(query, 'number');
};

const useShopRimOptions = ({ brandIds }: { brandIds?: number[] } = {}): FilterOptionSource<number> => {
  const { pageIndex, pageSize } = usePagination({ initialPageSize: 50 });

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedShopRims,
    input: { pageIndex, pageSize, ...(brandIds?.length ? { brandIds } : {}) },
    queryConfig: { staleTime: Infinity, select: toShopRimOptions }
  });

  return toFilterOptionSource(query, 'number');
};

const useRimInventoryDimensionOptions = (): FilterOptionSource<string> => {
  const { pageIndex, pageSize } = usePagination({ initialPageSize: 50 });

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedRimInventoryDimensions,
    input: { clearCache: false, pageIndex, pageSize },
    queryConfig: { staleTime: Infinity, select: toRimInventoryDimensionOptions }
  });

  return toFilterOptionSource(query, 'string');
};

const toShopRimBrandOptions = ({ results }: ShopRimBrandListApiResponse) =>
  results.map((brand) => ({ label: brand.name, value: brand.id }));

const toShopRimOptions = ({ results }: ShopRimListApiResponse) =>
  results.map((rim) => ({
    label: [rim.brand?.name, rim.model].filter(Boolean).join(' - ') || String(rim.id),
    value: rim.id
  }));

const toRimInventoryDimensionOptions = ({ results }: ShopRimInventoryDimensionsListApiResponse) =>
  results.map((dimension) => ({ label: `${dimension.dimension} (${dimension.count})`, value: dimension.dimension }));
